/* eslint-disable react-hooks/exhaustive-deps */
import { useApolloClient } from '@apollo/client/react/hooks/useApolloClient';
import transparentize from 'polished/lib/color/transparentize';
import React, { Dispatch, FC, SetStateAction, useContext, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { ActivePropertyContext } from '../../context/ActivePropertyContext';
import { UiStateContext } from '../../context/UiStateContext';
import { GET_MAP_PROPERTIES, GET_MAP_PROPERTIES_NO_BRANCHES } from '../../graphql/properties';
import { UseBuildInfo } from '../../hooks/useBuildInfo';
import { useDataLayerPush } from '../../hooks/useDataLayerPush';
import UnAvailableMapMarker from '../../images/maps/marker-black.png';
import BranchMapMarker from '../../images/maps/marker-branch.png';
import AvailableMapMarker from '../../images/maps/marker-red.png';
import { colors } from '../../styles/tokens';
import { Kfh_PropertyTypeInterface } from '../../types/graphqlTypes';
import { createQueryString } from '../../utils/queryStrings';
import { BranchBladeDynamic } from '../branches/BranchBladeDynamic';
import { Loader } from '../Loader';
import { Map, MapMarker } from '../Map';
import { Spacer } from '../Spacer';
import { PropertyCardBlade } from './PropertyCardBlade';
import { PropertyMapKey } from './PropertyMapKey';

interface PropertyMapProps {
  search?: Record<string, any>;
  isDevelopment?: boolean;
}

const PAGE_SIZE = 250;

const iconComponentPropertyAvailable = AvailableMapMarker;
const iconComponentPropertyUnAvailable = UnAvailableMapMarker;
const iconComponentBranch = BranchMapMarker;

const StyledRelative = styled.div`
  position: relative;
`;

const StyledLoaderWrap = styled.div`
  ${({ theme: { space, layers } }) => css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: ${space.lg};
    background-color: ${transparentize(0.2, colors.white.default)};
    z-index: ${layers.bladeOverlay};
  `};
`;

//
// search for properties based on the form results.
// loop each page adding to the array
// also grab the branch id and lat lng for each and pop these on array if not already in there
// build up the map markers
// add blade for property detail card or branch card
// how should we handle the loading of pagination?  soom sort of bar loader? and progresivly load the map markers
// handle moving to the search results page (pass state via navigate)
// what about the factes
export const PropertyMap: FC<PropertyMapProps> = ({ search, isDevelopment }) => {
  const { dataLayerPush } = useDataLayerPush();

  // Setup the search

  const client = useApolloClient();
  const [loading, setLoading] = useState(true);
  const [called, setCalled] = useState(false);
  const [searchString, setSearchString] = useState('');

  const { setActiveBlade } = useContext(UiStateContext);
  const buildInfo = UseBuildInfo();
  const [activeBranch, setActiveBranch] = useState();
  const [activePropertyLocal, setActivePropertyLocal] = useState();

  const [markers, setMarkers] = useState<MapMarker[]>([]);
  const handleOpenBranchBlade = (branch) => {
    setActiveBranch(branch);
    setActiveBlade('branch');

    dataLayerPush({
      event: 'map pin click',
      clickType: 'KFH Branch',
      clickHostname: undefined,
      clickPath: undefined,
      clickQueryString: undefined,
      clickBladeId: 'branch',
    });
  };
  const handleOpenPropertyBlade = (property) => {
    setActivePropertyLocal(property);
    setActiveBlade('propertyCard');

    dataLayerPush({
      event: 'map pin click',
      clickType: 'Property for sale',
      clickHostname: undefined,
      clickPath: undefined,
      clickQueryString: undefined,
      clickBladeId: 'propertyCard',
    });
  };
  useEffect(() => {
    const qs = createQueryString(search);

    const fetchAll = async () => {
      const allProperties: any[] = [];
      const allBranches: any[] = [];

      // Get first

      const { data: firstData } = await client.query({
        query: GET_MAP_PROPERTIES,
        variables: { ...search, page: 1, first: PAGE_SIZE, lastBuildDateTime: buildInfo?.lastBuild },
      });

      const { totalCount, edges } = firstData.properties.propertySearch.properties;
      const { branches } = firstData.properties.propertySearch;

      const totalPages = Math.ceil(totalCount / PAGE_SIZE);

      const remainingPages = totalPages - 1;

      // Add first page to the arrays
      if (edges?.length >= 1) {
        allProperties.push(...(edges as []));
      }
      if (branches?.length >= 1) {
        allBranches.push(...(branches as []));
      }

      // Get rest of the pages if there are any

      if (remainingPages >= 1) {
        const thePromises = [...Array(remainingPages)].map((_, i) =>
          client.query({
            query: GET_MAP_PROPERTIES_NO_BRANCHES,
            variables: { ...search, page: i + 1, first: PAGE_SIZE, lastBuildDateTime: buildInfo?.lastBuild },
          })
        );
        if (thePromises.length >= 1) {
          const moreData = await Promise.all(thePromises);

          // Note branches only come in the first request
          moreData.forEach((d) => {
            const { edges: moreEdges } = d.data.properties.propertySearch.properties;

            if (moreEdges?.length >= 1) {
              allProperties.push(...(moreEdges as []));
            }
          });
        }
      }

      const newMarkers: MapMarker[] = [];
      // Add all properties to the marker array

      // reduce allProperties to merge properties with same latitude longitude
      const reducedProperties = allProperties
        .filter(
          (edge) =>
            !!edge.node.latitude && !!edge.node.longitude && edge.node.latitude !== 0 && edge.node.longitude !== 0
        )
        .reduce((acc, curr) => {
          const property = curr.node;
          const { latitude, longitude } = property;
          const existing = acc.findIndex((a) => a.position.lat === latitude && a.position.lng === longitude);

          // if exsisting and not already in the array add to the array
          if (existing >= 0 && !acc[existing].multipleIds.includes(property.id)) {
            acc[existing].multipleIds.push(property.id);
            return acc;
          }

          const propertyMarker: MapMarker = {
            id: property.id,
            multipleIds: [property.id as string],
            type: property.available ? 'property-available' : 'property-unavailable',
            iconComponent: property.available ? iconComponentPropertyAvailable : iconComponentPropertyUnAvailable,
            position: {
              lat: property.latitude,
              lng: property.longitude,
            },
          };

          return [...acc, propertyMarker];
        }, []) as MapMarker[];
      // this needs to only return a new marker if new lat lng!!

      // find markers with multiple multipleIds
      // const multipleIds = reducedProperties.filter((m) => m.multipleIds.length > 1);

      const markersWithActions = reducedProperties.map((marker) => {
        marker.onClick = () => handleOpenPropertyBlade(marker.multipleIds);
        return marker;
      });

      newMarkers.push(...markersWithActions);
      // allProperties.forEach((edge: any) => {
      //   const property = edge.node;
      //   if (property.latitude && property.longitude) {
      //     const propertyMarker = {
      //       id: property.id,
      //       type: 'property.available ? property-available : property-unavailable',
      //       iconComponent: property.available ? iconComponentPropertyAvailable : iconComponentPropertyUnAvailable,
      //       onClick: () => handleOpenPropertyBlade(property.id),
      //       position: {
      //         lat: property.latitude,
      //         lng: property.longitude,
      //       },
      //     } as MapMarker;

      //     newMarkers.push(propertyMarker);
      //   }
      // });

      allBranches.forEach((branch) => {
        const position =
          search?.category === 'RESALE'
            ? {
                lat: branch.salesLat,
                lng: branch.salesLon,
              }
            : {
                lat: branch.lettingsLat,
                lng: branch.lettingsLon,
              };
        const branchMarker = {
          id: branch.id,
          type: 'branch',
          iconComponent: iconComponentBranch,
          onClick: () => handleOpenBranchBlade(branch.id),
          position,
        } as MapMarker;
        if (position.lat && position.lng) {
          newMarkers.push(branchMarker);
        } else {
          console.log('no position for branch', branch.id);
        }
      });
      // Set all the state
      setMarkers(newMarkers);
      setSearchString(qs);
      setLoading(false);
      setCalled(true);
    };

    if (qs !== searchString) {
      fetchAll();
    }
  }, [search]);

  return (
    <>
      <Spacer>
        <StyledRelative>
          {loading && (
            <StyledLoaderWrap>
              <Loader />
            </StyledLoaderWrap>
          )}

          {markers && <Map markers={markers || []} mapStyle="properties" />}
        </StyledRelative>
        <PropertyMapKey category={search?.category} />
      </Spacer>
      {activeBranch && <BranchBladeDynamic branchId={activeBranch} />}
      {activePropertyLocal && <PropertyCardBlade propertyId={activePropertyLocal} isDevelopment={isDevelopment} />}
    </>
  );
};

export default PropertyMap;
